import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef } from "react";

import type { ConversationItem } from "@/components/agents/conversation/types";
import { runLogScreenshotQuery } from "@/hooks/useConfigMigration";

/** Quantos prints baixam ao mesmo tempo. */
export const SCREENSHOT_PREFETCH_LIMIT = 3;
/** Quantos prints, dos mais recentes, entram na fila de pré-carga. */
export const SCREENSHOT_PREFETCH_RECENT = 20;

/**
 * Baixa em segundo plano os prints mais recentes do histórico que ainda não estão no
 * cache, do mais novo para o mais antigo, no máximo `SCREENSHOT_PREFETCH_LIMIT` por vez.
 * Só os `SCREENSHOT_PREFETCH_RECENT` últimos entram na fila: os demais baixam pela
 * miniatura, quando ela chega perto da tela. A miniatura usa a mesma consulta, então o
 * que já baixou aparece na hora e nada baixa duas vezes.
 *
 * O print cuja consulta falhou não volta à fila a cada print novo: quem tenta de novo é
 * a miniatura (e o "Try again" do modal), que é onde a pessoa vê a falha.
 */
export function usePrefetchScreenshots(
  items: ConversationItem[],
  media: { projectId: string; runId: string },
): void {
  const queryClient = useQueryClient();
  const { projectId } = media;
  const failed = useRef(new Set<string>());
  // `run/logId` de cada print: o do Nó 02 pode vir de um run anterior (`item.runId`).
  const shots = items
    .flatMap((item) =>
      item.kind === "screenshot" ? [`${item.runId ?? media.runId}/${item.logId}`] : [],
    )
    .reverse()
    .slice(0, SCREENSHOT_PREFETCH_RECENT)
    .join(",");

  useEffect(() => {
    let cancelled = false;
    let active = 0;
    const failedKey = (shot: string) => `${projectId}/${shot}`;
    const queryOf = (shot: string) => {
      const [runId, logId] = shot.split("/");
      return runLogScreenshotQuery(projectId, runId, Number(logId));
    };
    const queue = shots
      ? shots.split(",").filter((shot) => {
          if (failed.current.has(failedKey(shot))) return false;
          return queryClient.getQueryState(queryOf(shot).queryKey)?.status !== "success";
        })
      : [];
    const pump = () => {
      while (!cancelled && active < SCREENSHOT_PREFETCH_LIMIT && queue.length > 0) {
        const shot = queue.shift() as string;
        const query = queryOf(shot);
        active += 1;
        void queryClient.prefetchQuery(query).finally(() => {
          active -= 1;
          if (queryClient.getQueryState(query.queryKey)?.status === "error") {
            failed.current.add(failedKey(shot));
          }
          pump();
        });
      }
    };
    pump();
    return () => {
      cancelled = true;
    };
  }, [queryClient, projectId, shots]);
}
